import { FormattedMessage, injectIntl } from 'react-intl';
import React, { useEffect, useRef, useState } from 'react';
import { useRoutes } from './apiHooks';
import Loader from '../../components/loader';
import ErrorMessage from '../../components/errorMessage';
import utils from '../../utils/utils';
import DATE_FORMAT from '../../components/constants/dateFormat';
import { useQueryClient } from 'react-query';
import RouteTable from './components/routeTable';
import apiCalls from '../../utils/apiCalls';
import { faFilePdf, faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { pdf } from '@react-pdf/renderer';
import AdminRoutePlanRoutesPdf from './adminRoutePlanRoutesPdf';
import { saveAs } from 'file-saver';
import classNames from 'classnames';
import ITEM_CUSTOM_PACKING_TYPE from '../../components/constants/itemCustomPackingType';
import AdminRoutePlanCustomTypeRoutesPdf from './adminRoutePlanCustomTypeRoutesPdf';
import AdminRoutePlanCustomTypeDeliveriesPdf from './adminRoutePlanCustomTypeDeliveriesPdf';

const AdminRoutePlanV2DeliveryDayRoutes = ({ routePlan, selectedDeliveryDay }) => {
  const [deliveries, setDeliveries] = useState(null);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveriesError, setDeliveriesError] = useState(null);
  const [compact, setCompact] = useState(true);
  const [typedQuery, setTypedQuery] = useState(''); // for detecting input change
  const [selectedCustomPackingTypes, setSelectedCustomPackingTypes] = useState([]);
  const [query, setQuery] = useState(null); // for triggering actual search
  const queryTimer = useRef(null);

  const [showOrderInfo, setShowOrderInfo] = useState(false);

  const {
    routes,
    isFetching,
    isLoading: routesLoading,
    isError: isRoutesError,
    error: routesError,
    refetch,
  } = useRoutes({
    deliveryDayId: selectedDeliveryDay.id,
    version: 2,
    showOrderInfo,
    refetchInterval: showOrderInfo ? null : 10000,
    deliveryIds: deliveries?.length ? deliveries.map((d) => d.id).join(',') : null,
  });

  useEffect(() => {
    refetch();
  }, [showOrderInfo]);

  useEffect(() => {
    if (!deliveries?.length) {
      refetch(); // reload when clearing search to avoid showOrderInfo query only showing search result routes
    }
  }, [deliveries]);

  const queryClient = useQueryClient();

  function getDeliveries() {
    setDeliveriesLoading(true);
    apiCalls
      .getDeliveries({
        query,
        deliveryDayId: selectedDeliveryDay.id,
        version: 2,
      })
      .then(({ data: { deliveries: resultDeliveries } }) => {
        setDeliveries(resultDeliveries);
        setDeliveriesLoading(false);
      })
      .catch((errors) => {
        setDeliveriesLoading(false);
        setDeliveriesError(utils.getErrorMessageFromResponse(errors));
      });
  }

  useEffect(() => {
    const newQuery = selectedCustomPackingTypes.length ? `customTypes:${selectedCustomPackingTypes.join(',')}` : '';
    setTypedQuery(newQuery);
  }, [selectedCustomPackingTypes]);

  useEffect(() => {
    clearTimeout(queryTimer.current);
    queryTimer.current = setTimeout(() => {
      setQuery(typedQuery);
    }, 500);
  }, [typedQuery]);

  useEffect(() => {
    if (query) {
      getDeliveries();
    } else {
      setShowOrderInfo(false);
      setDeliveries(null);
    }
  }, [query]);

  const deliveryDayDate = utils.getFormattedDateFromString(selectedDeliveryDay.deliveryDate, DATE_FORMAT.onlyDate);

  const filteredRoutes = deliveries?.length
    ? routes.filter((route) => route.deliveries.some((d) => deliveries.some((delivery) => delivery.id === d.id)))
    : routes;
  const routesGroupedByDeliveryFrom = filteredRoutes ? utils.groupBy(filteredRoutes, 'deliveryFrom') : {};

  return (
    <div>
      <Loader show={routesLoading} />
      <h3 className="m-b-3">
        <FormattedMessage id={'admin.route.planning.delivery.day.routes.title'} values={{ date: deliveryDayDate }} />
      </h3>

      <Loader show={deliveriesLoading} />
      <ErrorMessage error={deliveriesError} />
      <div className="m-b-1">
        {isRoutesError ? <ErrorMessage error={utils.getErrorMessageFromResponse(routesError)} /> : ''}
      </div>

      <div className="m-b-6 row">
        <div className="col-12 col-md-8">
          <button
            onClick={() => {
              if (showOrderInfo) {
                setShowOrderInfo(false);
              } else {
                queryClient.invalidateQueries('routes');
              }
            }}
            className={`btn btn-small btn-primary m-r-2 m-b-2 ${routesLoading || isFetching ? 'btn-loading' : ''}`}
            disabled={routesLoading || isFetching}
          >
            <FormattedMessage id={'admin.route.planning.edit.actions.refresh'} />
          </button>
          <button
            onClick={() => {
              setShowOrderInfo(true);
            }}
            className={`btn btn-small btn-default m-r-2 m-b-2 ${routesLoading || isFetching ? 'btn-loading' : ''}`}
            disabled={routesLoading || isFetching}
          >
            <FormattedMessage id={'admin.route.planning.edit.actions.refresh.with.content'} />
          </button>
          <button
            onClick={() => {
              setCompact(!compact);
            }}
            className={`btn btn-small btn-default m-r-2 m-b-2`}
          >
            <FormattedMessage id={'admin.route.planning.edit.actions.show.content'} />
          </button>
        </div>
        <div className="col-12 col-md-4 text-right">
          <div className="form-group field field-string d-inline-block">
            <input
              className="form-control"
              placeholder="Search"
              type="text"
              value={typedQuery}
              onChange={(e) => setTypedQuery(e.target.value)}
            />
            <p className="help-block">
              <FormattedMessage id={'admin.route.planning.edit.search.help'} />
            </p>
          </div>
        </div>
        <div className="col-12 m-t-2">
          <div className="m-b-1">
            {Object.keys(ITEM_CUSTOM_PACKING_TYPE).map((key) => {
              const type = ITEM_CUSTOM_PACKING_TYPE[key];

              const enabledIndex = selectedCustomPackingTypes.findIndex((st) => st === type.name);
              const badgeClasses = classNames({
                'badge m-r-1': true,
                'badge-secondary': enabledIndex === -1,
                'badge-primary': enabledIndex !== -1,
              });

              return (
                <span
                  key={key}
                  className={badgeClasses}
                  onClick={() => {
                    const newSelectedTypes = selectedCustomPackingTypes.slice();
                    if (enabledIndex >= 0) {
                      newSelectedTypes.splice(enabledIndex, 1);
                    } else {
                      newSelectedTypes.push(type.name);
                    }

                    setSelectedCustomPackingTypes(newSelectedTypes);
                  }}
                >
                  {type.icon} <FormattedMessage id={`item.form.field.custom.packing.type.name.${type.name}`} />
                </span>
              );
            })}
          </div>

          {selectedCustomPackingTypes.length ? (
            <div>
              <button
                disabled={!showOrderInfo}
                className="btn btn-normal-link m-r-2"
                onClick={async () => {
                  const routeObjects = routes.map((route) => {
                    const deliveriesWithTypes = route.deliveries.filter((delivery) =>
                      selectedCustomPackingTypes.every((customType) => {
                        return (
                          delivery.deliveryOrderBatch &&
                          delivery.deliveryOrderBatch.customTypes &&
                          delivery.deliveryOrderBatch.customTypes.some((ct) => ct.type === customType)
                        );
                      })
                    );

                    return {
                      routeId: route.id,
                      deliveryFrom: route.deliveryFrom,
                      deliveries: deliveriesWithTypes,
                    };
                  });

                  const doc = <AdminRoutePlanCustomTypeRoutesPdf routeObjects={routeObjects} />;
                  const asPdf = pdf(); // {} is important, throws without an argument
                  asPdf.updateContainer(doc);
                  const blob = await asPdf.toBlob();
                  saveAs(blob, `route-custom-types.pdf`);
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} />{' '}
                {showOrderInfo ? (
                  <FormattedMessage id={'admin.route.planning.edit.custom.types.actions.pdf.routes'} />
                ) : (
                  <FormattedMessage
                    id={'admin.route.planning.edit.custom.types.actions.show.order.info.before.download'}
                  />
                )}
              </button>
              <button
                disabled={!showOrderInfo}
                className="btn btn-normal-link m-r-2"
                onClick={async () => {
                  const deliveries = routes
                    .map((route) => {
                      const deliveriesWithTypes = route.deliveries.filter((delivery) =>
                        selectedCustomPackingTypes.every((customType) => {
                          return (
                            delivery.deliveryOrderBatch &&
                            delivery.deliveryOrderBatch.customTypes &&
                            delivery.deliveryOrderBatch.customTypes.some((ct) => ct.type === customType)
                          );
                        })
                      );

                      const deliveryFrom = utils.getFormattedDateFromString(route.deliveryFrom, DATE_FORMAT.onlyTime);

                      return deliveriesWithTypes.map((delivery) => ({
                        deliveryFrom,
                        delivery: delivery,
                      }));
                    })
                    .flat();

                  const doc = <AdminRoutePlanCustomTypeDeliveriesPdf deliveries={deliveries} />;
                  const asPdf = pdf(); // {} is important, throws without an argument
                  asPdf.updateContainer(doc);
                  const blob = await asPdf.toBlob();
                  saveAs(blob, `deliveries-custom-types.pdf`);
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} />{' '}
                {showOrderInfo ? (
                  <FormattedMessage id={'admin.route.planning.edit.custom.types.actions.pdf.deliveries'} />
                ) : (
                  <FormattedMessage
                    id={'admin.route.planning.edit.custom.types.actions.show.order.info.before.download'}
                  />
                )}
              </button>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>

      {deliveries ? (
        <div className="m-b-6">
          <FontAwesomeIcon icon={faSearch} />{' '}
          <FormattedMessage
            id={'admin.route.planning.delivery.day.routes.search.results'}
            values={{ count: deliveries.length }}
          />
        </div>
      ) : (
        ''
      )}

      {Object.keys(routesGroupedByDeliveryFrom).map((deliveryFrom, index) => {
        const deliveryFromRoutes = routesGroupedByDeliveryFrom[deliveryFrom];
        const formattedTimeRange = utils.getFormattedDateRangeFromStrings(
          deliveryFrom,
          deliveryFromRoutes[0].deliveryTo
        );

        const packingStats = deliveryFromRoutes.reduce((result, route) => {
          if (result[route.status]) {
            result[route.status] += 1;
          } else {
            result[route.status] = 1;
          }

          return result;
        }, {});

        return (
          <div key={index}>
            <h3 className="m-b-3">{formattedTimeRange}</h3>

            <div className="m-b-3">
              <button
                className="btn btn-normal-link m-r-2"
                onClick={async () => {
                  const doc = (
                    <AdminRoutePlanRoutesPdf
                      routes={deliveryFromRoutes}
                      deliveryFrom={utils.getDateFromString(deliveryFrom)}
                    />
                  );
                  const asPdf = pdf(); // {} is important, throws without an argument
                  asPdf.updateContainer(doc);
                  const blob = await asPdf.toBlob();
                  saveAs(blob, `route-${formattedTimeRange}.pdf`);
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} />{' '}
                <FormattedMessage id={'admin.route.planning.delivery.day.routes.download.pdf'} />
              </button>
            </div>
            <div className="m-b-3">
              {Object.keys(packingStats).map((status, index) => (
                <div key={index}>
                  {status}: {packingStats[status]}
                </div>
              ))}
            </div>
            <div className="row">
              {deliveryFromRoutes.map((route) => (
                <RouteTable
                  deliverySearchResults={deliveries}
                  key={route.id}
                  route={route}
                  selectedDeliveryDay={selectedDeliveryDay}
                  routePlan={routePlan}
                  routes={routes}
                  compact={deliveries?.length ? false : compact}
                />
              ))}
            </div>
            <hr className="m-t-6 m-b-6" />
          </div>
        );
      })}
    </div>
  );
};

export default injectIntl(AdminRoutePlanV2DeliveryDayRoutes);
